import { useCallback, useEffect, useRef } from 'react';
import { handleFetchInfraDevicesAPI, registerTagAPI } from '@/api/storeManagement/registration';
import { useAppContext } from '@/provider/contexts';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { HandleFetchInfraDevicesRequest } from '@/types/pages/storeManagement/registration';
import {
    DEVICE_TYPE,
    InfraDevice,
    PaginationModel,
    REGISTRATION_MODE,
    SortModel,
    TAB_TYPE,
} from '@/types/pages/storeManagement/registration/registrationTypes';
import { deriveInfraDeviceState, deriveInfraTagState } from '@/utils/registrationUtils';

const DEVICE_TYPE_MAP = {
    [TAB_TYPE.AP]: 'ap',
    [TAB_TYPE.RT]: 'duo',
    [TAB_TYPE.TAG]: 'tag',
} as const;

type DeviceType = keyof typeof DEVICE_TYPE_MAP;

type TabResolveStrategy = 'initial' | 'preserve-current';

type FetchAllDevicesOptions = {
    signal?: AbortSignal;
    currentTab: TAB_TYPE;
    strategy?: TabResolveStrategy;
};

type FetchAllDevicesResult = {
    infraDevice: InfraDevice[];
    installationMode: REGISTRATION_MODE;
    effectiveTab: TAB_TYPE;
    fallbackTab: TAB_TYPE;
    disabledTabs: TAB_TYPE[];
    isCurrentTabValid: boolean;
};

type FetchInfraDevicesTableDataOptions = {
    signal?: AbortSignal;
    tab: TAB_TYPE;
    paginationModel: PaginationModel;
    sortModel: SortModel;
    searchMac: string;
    infraDevice: InfraDevice[];
    registrationMode: REGISTRATION_MODE;
};

const resolveRegistrationTabState = ({
    infraDevice,
    installationMode,
    currentTab,
    strategy,
}: {
    infraDevice: InfraDevice[];
    installationMode: REGISTRATION_MODE;
    currentTab: TAB_TYPE;
    strategy: TabResolveStrategy;
}) => {
    const { hasTag, hasApConfirmed, hasApUnlock, hasRTConfirmed } = deriveInfraDeviceState(infraDevice);
    const { isOnProgress, tagInfo, isAllUnConfirm } = deriveInfraTagState(infraDevice);

    let disabledTabs: TAB_TYPE[] = [];
    let fallbackTab = TAB_TYPE.NONE;
    let tagState = {
        isOnProgress: false,
        status: tagInfo,
    };

    if (installationMode === REGISTRATION_MODE.NORMAL_MODE) {
        if (hasTag) {
            disabledTabs = [TAB_TYPE.AP, TAB_TYPE.RT];
            fallbackTab = TAB_TYPE.TAG;
        } else {
            disabledTabs = [TAB_TYPE.AP, TAB_TYPE.RT, TAB_TYPE.TAG];
            fallbackTab = TAB_TYPE.NONE;
        }

        const isCurrentTabValid = currentTab !== TAB_TYPE.NONE && !disabledTabs.includes(currentTab);
        const effectiveTab = strategy === 'preserve-current' && isCurrentTabValid ? currentTab : fallbackTab;

        return {
            disabledTabs,
            fallbackTab,
            effectiveTab,
            isCurrentTabValid,
            tagState,
        };
    }

    if (!hasApConfirmed) {
        disabledTabs = [TAB_TYPE.RT, TAB_TYPE.TAG];
        fallbackTab = TAB_TYPE.AP;
    } else if (!hasRTConfirmed || !hasApUnlock) {
        disabledTabs = [TAB_TYPE.TAG];
        fallbackTab = TAB_TYPE.AP;
    } else if (!isAllUnConfirm && isOnProgress) {
        disabledTabs = [TAB_TYPE.AP];
        fallbackTab = TAB_TYPE.RT;
        tagState = {
            isOnProgress,
            status: tagInfo,
        };
    } else {
        disabledTabs = [];
        fallbackTab = TAB_TYPE.AP;
        tagState = {
            isOnProgress: false,
            status: tagInfo,
        };
    }

    const isCurrentTabValid = currentTab !== TAB_TYPE.NONE && !disabledTabs.includes(currentTab);
    const effectiveTab = strategy === 'preserve-current' && isCurrentTabValid ? currentTab : fallbackTab;

    return {
        disabledTabs,
        fallbackTab,
        effectiveTab,
        isCurrentTabValid,
        tagState,
    };
};

export const useRegistrationAPI = () => {
    const { manageStoreId, setNotification } = useAppContext();
    const { dispatch } = useRegistrationState();

    const fetchAllDevicesIdRef = useRef(0);
    const fetchInfraTableIdRef = useRef(0);

    useEffect(() => {
        fetchAllDevicesIdRef.current = 0;
        fetchInfraTableIdRef.current = 0;
    }, [manageStoreId]);

    const fetchAllDevices = useCallback(
        async ({
            signal,
            currentTab,
            strategy = 'preserve-current',
        }: FetchAllDevicesOptions): Promise<FetchAllDevicesResult | null> => {
            if (!manageStoreId) {
                return null;
            }

            const fetchId = fetchAllDevicesIdRef.current + 1;
            fetchAllDevicesIdRef.current = fetchId;

            const postData = {
                store_id: manageStoreId,
                f_confirmed: null,
                f_mac: '',
                data_per_page: 100000,
            } as HandleFetchInfraDevicesRequest;

            try {
                const {
                    data: { data, isSuccess, message },
                } = await handleFetchInfraDevicesAPI(postData, signal);

                if (signal?.aborted || fetchAllDevicesIdRef.current !== fetchId) {
                    return null;
                }

                if (!isSuccess) {
                    setNotification({ message, severity: 'error' });
                    return null;
                }

                const infraDevice = data.aaData;
                const installationMode = data.installation_mode;

                const tabState = resolveRegistrationTabState({
                    infraDevice,
                    installationMode,
                    currentTab,
                    strategy,
                });

                dispatch({
                    type: 'SET_REGISTRATION_MODE',
                    payload: installationMode,
                });

                dispatch({
                    type: 'SET_INFRA_DEVICES',
                    payload: infraDevice,
                });

                dispatch({
                    type: 'TOGGLE_LABEL_DISABLE',
                    payload: {
                        label: tabState.disabledTabs,
                    },
                });

                dispatch({
                    type: 'SET_TAG',
                    payload: tabState.tagState,
                });

                if (tabState.effectiveTab !== currentTab) {
                    dispatch({
                        type: 'SET_TAB',
                        payload: tabState.effectiveTab,
                    });
                }

                dispatch({
                    type: 'TOGGLE_LOADING',
                    payload: {
                        key: 'storeStateLoading',
                        isLoading: false,
                    },
                });

                return {
                    infraDevice,
                    installationMode,
                    effectiveTab: tabState.effectiveTab,
                    fallbackTab: tabState.fallbackTab,
                    disabledTabs: tabState.disabledTabs,
                    isCurrentTabValid: tabState.isCurrentTabValid,
                };
            } catch (error: unknown) {
                if (error instanceof Error && error.message === 'canceled') {
                    return null;
                }

                setNotification({
                    message: 'Failed to fetch data',
                    severity: 'error',
                });

                return null;
            }
        },
        [dispatch, manageStoreId, setNotification],
    );

    const fetchInfraDevicesTableData = useCallback(
        async ({
            signal,
            tab,
            paginationModel,
            sortModel,
            searchMac,
            infraDevice,
            registrationMode,
        }: FetchInfraDevicesTableDataOptions) => {
            if (!manageStoreId) {
                return;
            }

            if (tab === TAB_TYPE.NONE) {
                return;
            }

            const fetchId = fetchInfraTableIdRef.current + 1;
            fetchInfraTableIdRef.current = fetchId;

            const gwList =
                tab === TAB_TYPE.RT && registrationMode === REGISTRATION_MODE.INSTALLATION_MODE
                    ? infraDevice.filter((item) => item.device_type_id === DEVICE_TYPE.AP).map((item) => item.gw_mac)
                    : [];

            const postData = {
                store_id: manageStoreId as number,
                f_confirmed: null,
                f_mac: searchMac,
                current_page: paginationModel.page + 1,
                data_per_page: paginationModel.pageSize,
                sort_field: sortModel.field,
                sort_order: sortModel.sort,
                f_gw_mac: gwList,
                f_device_type: DEVICE_TYPE_MAP[tab as DeviceType],
            };

            dispatch({
                type: 'SET_TABLE_STATE',
                payload: {
                    status: 'loading',
                },
            });

            try {
                const {
                    data: { data, isSuccess, message },
                } = await handleFetchInfraDevicesAPI(postData, signal);

                if (signal?.aborted || fetchInfraTableIdRef.current !== fetchId) {
                    return;
                }

                if (!isSuccess) {
                    setNotification({ message, severity: 'error' });
                    return;
                }

                if (tab === TAB_TYPE.TAG && registrationMode === REGISTRATION_MODE.INSTALLATION_MODE) {
                    const { tagForRegisterList } = deriveInfraTagState(infraDevice);

                    if (tagForRegisterList.length > 0) {
                        await registerTagAPI({
                            store_id: manageStoreId as number,
                            mac_list: tagForRegisterList,
                        });
                    }
                }

                dispatch({
                    type: 'SET_TABLE_STATE',
                    payload: {
                        status: 'success',
                        infraDeviceInfo: data.aaData,
                        rowCount: data.total_records,
                        doingTask: false,
                    },
                });
            } catch (error: unknown) {
                if (error instanceof Error && error.message === 'canceled') {
                    return;
                }

                setNotification({
                    message: 'Failed to fetch data',
                    severity: 'error',
                });
            } finally {
                if (!signal?.aborted && fetchInfraTableIdRef.current === fetchId) {
                    dispatch({
                        type: 'SET_TABLE_STATE',
                        payload: {
                            status: 'idle',
                        },
                    });
                }
            }
        },
        [dispatch, manageStoreId, setNotification],
    );

    return {
        fetchAllDevices,
        fetchInfraDevicesTableData,
    };
};
